import { useContext } from 'react';
import { Context } from '../../../App';

const toNum = (v) => (Number.isFinite(+v) ? +v : null);
const fmt = (v, d = 2) => (v === null ? '—' : Number(v).toFixed(d));

export default function InteractiveSketch({ name, z1, z2, df, b, l, q, clayType, drainageType, mv, cv, t, variant }) {
  const { theme: appTheme } = useContext(Context);

  // --- Canvas (pure 2D)
  const VIEWBOX_WIDTH = 980;
  const VIEWBOX_HEIGHT = 450;
  const MARGIN = 32;

  const groundSurfaceY = 78; // y of ground line
  const canvasBottomY = VIEWBOX_HEIGHT - MARGIN;

  // Plot frame (2D only)
  const plotLeftX = MARGIN;
  const plotRightX = VIEWBOX_WIDTH - MARGIN;
  const plotTopY = groundSurfaceY;
  const plotBottomY = canvasBottomY;

  // --- Inputs
  const Z1 = toNum(z1);
  const Z2 = toNum(z2);
  const Df = toNum(df);
  const B = toNum(b);
  const L = toNum(l);
  const Q = toNum(q); // currently unused

  // --- Vertical scale (depth from ground) — freeze layout if Z1>7 or Z2>10
  const PX_PER_M = 30;
  const FREEZE = (Z1 !== null && Z1 > 7) || (Z2 !== null && Z2 > 10);
  const DEPTH_CAP_M = FREEZE ? 10 : Infinity;
  const depthToY = (depthM) => plotTopY + Math.min(DEPTH_CAP_M, Math.max(0, depthM || 0)) * PX_PER_M;

  // Visual-only (capped) depths for drawing; numbers for labels remain true inputs
  const Z1V = FREEZE ? (Z1 !== null ? Math.min(Z1, 7) : 6) : Z1;
  const Z2V = FREEZE ? (Z2 !== null ? Math.min(Z2, 10) : 12) : Z2;
  // Visual footing depth: if layout is frozen and the real footing lies inside the real clay,
  // draw it centered inside the visual clay band so the compressible zone stays visible.
  const DfV = FREEZE && Z1 !== null && Z2 !== null && Df !== null && Df > Z1 && Df < Z2 ? (Z1V + Z2V) / 2 : Df;

  // --- Clay band (full width) — draw with visual depths
  const clayTopY = Z1V !== null ? depthToY(Z1V) : depthToY(6);
  const clayBottomY = Z2V !== null ? depthToY(Z2V) : depthToY(12);

  // --- Foundation geometry (2D)
  const baseWidthPx = Math.max(160, Math.min(280, (B ?? 6) * 22));
  const baseThicknessPx = 25;
  const baseUndersideY = DfV !== null ? depthToY(DfV) : depthToY(2.0);
  const baseTopY = baseUndersideY - baseThicknessPx;
  const baseLeftX = (plotLeftX + plotRightX) * 0.5 - baseWidthPx / 2;

  // Pedestal (centered)
  const pedestalWidthPx = baseWidthPx * 0.2;
  const pedestalHeightPx = baseThicknessPx * 2;
  const pedestalLeftX = baseLeftX + baseWidthPx / 2 - pedestalWidthPx / 2;
  const pedestalTopY = baseTopY - pedestalHeightPx;

  // --- Visibility: footing below view? (disabled when layout is frozen)
  const footingBelowView = FREEZE ? false : Df !== null && baseUndersideY >= plotBottomY;

  const theme = variant === 'pdf-light' ? 'light' : appTheme;

  // --- Theme
  const line = theme === 'light' ? '#111' : '#9a9a9a';
  const text = theme === 'light' ? '#111' : '#ffffff';
  const conc = theme === 'light' ? '#ffffff' : '#2b2b2b';
  const sandDot = theme === 'light' ? '#bcbcbc' : '#d9d9d9';
  const clayX = theme === 'light' ? '#6f6f6f' : '#cfcfcf';

  let midDepth;
  if (Df <= Z1 && Df < Z2) {
    midDepth = (Z2 + Z1) / 2 - Df;
  } else if (Df >= Z1 && Df < Z2) {
    midDepth = (Z2 + Df) / 2 - Df;
  } else {
    midDepth = 0;
  }

  return (
    <div className="sketch-rail">
      <div className="sketch-wrap">
        <svg
          viewBox={`0 0 ${VIEWBOX_WIDTH} ${VIEWBOX_HEIGHT}`}
          width="100%"
          style={{ display: 'block', maxWidth: '1120px', margin: '0 auto' }}
          aria-label="Consolidation sketch (2D)"
        >
          <defs>
            <pattern id="sandDots" width="10" height="10" patternUnits="userSpaceOnUse">
              <circle cx="1.5" cy="1.5" r="0.9" fill={sandDot} />
            </pattern>
            <pattern id="clayX" width="20" height="20" patternUnits="userSpaceOnUse">
              <path d="M0 0 L20 20 M20 0 L0 20" stroke={clayX} strokeWidth="0.3" />
            </pattern>

            <pattern id="compressibleHatch" width="8" height="8" patternUnits="userSpaceOnUse">
              <rect width="8" height="8" fill="none" />
              {/* reddish hatch */}
              <path
                d="M-2,2 L2,-2 M0,8 L8,0 M6,10 L10,6"
                stroke={theme === 'light' ? '#c44' : '#ff6b6b'}
                strokeWidth="1"
                opacity="0.75"
              />
            </pattern>

            <marker id="arr" viewBox="0 0 10 10" refX="5" refY="5" markerWidth="6" markerHeight="6" orient="auto">
              <path d="M0 0 L10 5 L0 10Z" fill={line} />
            </marker>
            <marker
              id="arrowStart"
              viewBox="0 0 10 10"
              refX="5"
              refY="5"
              markerWidth="6"
              markerHeight="6"
              orient="auto-start-reverse"
            >
              <path d="M0 0 L10 5 L0 10Z" fill={line} />
            </marker>
            <marker id="arrRed" viewBox="0 0 10 10" refX="5" refY="5" markerWidth="6" markerHeight="6" orient="auto">
              <path d="M0 0 L10 5 L0 10Z" fill={theme === 'light' ? '#c44' : '#ff6b6b'} />
            </marker>

            <marker
              id="arrowStartRed"
              viewBox="0 0 10 10"
              refX="5"
              refY="5"
              markerWidth="6"
              markerHeight="6"
              orient="auto-start-reverse"
            >
              <path d="M0 0 L10 5 L0 10Z" fill={theme === 'light' ? '#c44' : '#ff6b6b'} />
            </marker>
            <mask id="maskWithoutClay">
              {/* keep everything visible */}
              <rect x={plotLeftX} y={plotTopY} width={plotRightX - plotLeftX} height={plotBottomY - plotTopY} fill="white" />
              {/* hide the clay band */}
              <rect
                x={plotLeftX}
                y={clayTopY}
                width={plotRightX - plotLeftX}
                height={Math.max(0, clayBottomY - clayTopY)}
                fill="black"
              />
            </mask>
          </defs>
          {/* Ground block */}
          <rect
            x={plotLeftX}
            y={plotTopY}
            width={plotRightX - plotLeftX}
            height={plotBottomY - plotTopY}
            fill="url(#sandDots)"
            mask="url(#maskWithoutClay)"
          />

          {/* frame outline drawn separately to avoid overlap thinning */}
          <rect
            x={plotLeftX}
            y={plotTopY}
            width={plotRightX - plotLeftX}
            height={plotBottomY - plotTopY}
            fill="none"
            stroke={line}
            strokeWidth="1.25"
          />

          {/* Ground label */}
          <text x={plotLeftX + 150} y={plotTopY - 10} fontSize="14" fill={text}>
            Ground Surface
          </text>
          {/* Ground level symbol (standard 45° indicator) */}
          <g stroke="currentColor" strokeWidth="1.2" fill="none" transform={`translate(${150}, ${0})`}>
            {/* Left 45° line */}
            <line x1={MARGIN - 6} y1={groundSurfaceY + 6} x2={MARGIN} y2={groundSurfaceY} />
            {/* Right 45° line */}
            <line x1={MARGIN + 6} y1={groundSurfaceY + 6} x2={MARGIN} y2={groundSurfaceY} />
            {/* Right 45° line */}
            <line x1={MARGIN + 12} y1={groundSurfaceY + 6} x2={MARGIN + 6} y2={groundSurfaceY} />
          </g>

          {/* Clay band */}
          <rect
            x={plotLeftX}
            y={clayTopY}
            width={plotRightX - plotLeftX}
            height={Math.max(0, clayBottomY - clayTopY)}
            fill="url(#clayX)"
            stroke={line}
            strokeWidth="1"
          />

          {Z1 !== null &&
            Z2 !== null &&
            Df !== null &&
            (() => {
              // vertical limits for compressible zone based on Df vs. clay [Z1,Z2]
              let yStart = null;
              let yEnd = null;

              if (Df <= Z1) {
                // foundation above clay: full clay thickness under the base
                yStart = Math.min(clayTopY, plotBottomY);
                yEnd = Math.min(clayBottomY, plotBottomY);
              } else if (Df < Z2) {
                // foundation bottom inside clay: from base underside down to clay bottom
                yStart = Math.min(baseUndersideY, plotBottomY);
                yEnd = Math.min(clayBottomY, plotBottomY);
              } else {
                // foundation bottom below clay: no compressible layer
                yStart = null;
                yEnd = null;
              }

              // horizontal limits: directly beneath the foundation plan (base)
              const compX = baseLeftX;
              const compW = baseWidthPx;

              if (yStart !== null && yEnd !== null && yEnd > yStart && compW > 1) {
                const midY = (yStart + yEnd) / 2;
                const hatchStroke = theme === 'light' ? '#c44' : '#ff6b6b';

                return (
                  <g aria-label="Compressible zone under foundation">
                    {/* hatched zone */}
                    <rect
                      x={compX}
                      y={yStart}
                      width={compW}
                      height={yEnd - yStart}
                      fill="url(#compressibleHatch)"
                      stroke={hatchStroke}
                      strokeWidth="0.8"
                      opacity="0.9"
                    />
                    {/* center line for consolidation calc */}
                    <line
                      x1={compX}
                      y1={midY}
                      x2={compX + compW}
                      y2={midY}
                      stroke={hatchStroke}
                      strokeDasharray="6 4"
                      strokeWidth="1.2"
                    />
                    {/* midpoint label */}
                    <text x={compX - 10} y={midY + 4} textAnchor="end" fontSize="13" fill={hatchStroke}>
                      Midpoint of compressible zone
                    </text>
                  </g>
                );
              }
              return null;
            })()}

          {/* Dimension from foundation bottom to center of compressible zone */}
          {Z1 !== null &&
            Z2 !== null &&
            Df !== null &&
            Z2 > Z1 &&
            (() => {
              let yStart = null;
              let yEnd = null;
              const baseUndersideYc = Math.min(baseUndersideY, clayBottomY - 2);

              if (Df <= Z1) {
                // foundation above clay → full clay
                yStart = baseUndersideYc;
                yEnd = (clayTopY + clayBottomY) / 2;
              } else if (Df < Z2) {
                // foundation inside clay → from base to mid of compressed part
                yStart = baseUndersideYc;
                yEnd = (baseUndersideYc + clayBottomY) / 2;
              } // ensure the arrow stays inside visible clay and is at least 8 px tall
              if (yStart !== null && yEnd !== null) {
                // keep within clay
                yStart = Math.max(yStart, clayTopY + 2);
                yEnd = Math.min(yEnd, clayBottomY - 2);
                // enforce a minimum height
                if (yEnd - yStart < 8) {
                  yStart = Math.max(clayTopY + 2, yEnd - 8);
                }
              } else {
                // below clay → no line
                return null;
              }
              // if clamped or invalid, skip drawing ΔH
              if (!(yStart !== null && yEnd !== null && yEnd > yStart)) {
                return null;
              }

              const dimX = baseLeftX + baseWidthPx / 2; // middle of foundation
              const midY = (yStart + yEnd) / 2;
              const hatchStroke = theme === 'light' ? '#c44' : '#ff6b6b';

              return (
                <g aria-label="Compression distance dimension">
                  {/* dashed guide */}
                  <line x1={dimX} y1={yStart} x2={dimX} y2={yEnd} stroke={hatchStroke} strokeWidth="1" strokeDasharray="5 4" />
                  {/* dimension line with arrows */}
                  <line
                    x1={dimX}
                    y1={yStart + 2}
                    x2={dimX}
                    y2={yEnd - 2}
                    stroke={hatchStroke}
                    strokeWidth="1.2"
                    markerStart="url(#arrowStartRed)"
                    markerEnd="url(#arrRed)"
                  />

                  {/* label centered */}
                  <g transform={`rotate(-90 ${dimX - 12} ${midY})`}>
                    <text x={dimX - 12} y={midY + 5} textAnchor="middle" fontSize="13" fill={hatchStroke}>
                      z = {fmt(midDepth, 2)} m
                    </text>
                  </g>
                </g>
              );
            })()}

          {/* Layer labels */}
          {Z1 !== null && Z2 !== null && Z2 > Z1 && Z1 !== 0 && (
            <>
              <text x={plotLeftX + 820} y={Math.max(plotTopY + 14, clayTopY - 16)} fontSize="14" fill={text}>
                Stiff Layer
              </text>
            </>
          )}

          {Z1 !== null && Z2 !== null && Z2 > Z1 && (
            <>
              <text
                x={plotLeftX + 820}
                y={Math.min(plotBottomY - 20, Math.max(plotTopY + 20, (clayTopY + clayBottomY) / 2 + 6))}
                fontSize="14"
                fill={text}
              >
                Clay Layer
              </text>
            </>
          )}

          {Z1 !== null && Z2 !== null && Z2 > Z1 && (
            <>
              <text x={plotLeftX + 820} y={Math.min(plotBottomY - 8, clayBottomY + 26)} fontSize="14" fill={text}>
                Stiff Layer
              </text>
            </>
          )}

          {/* z1 */}
          {Z1 !== null && Z2 !== null && Z1 > 0 && Z2 > 0 && Z2 > Z1 && (
            <g>
              <line
                x1={plotLeftX + 44}
                y1={plotTopY}
                x2={plotLeftX + 44}
                y2={clayTopY}
                stroke={line}
                strokeWidth="1"
                strokeDasharray="5 4"
              />
              <line
                x1={plotLeftX + 44}
                y1={plotTopY + 2}
                x2={plotLeftX + 44}
                y2={clayTopY - 2}
                stroke={line}
                strokeWidth="1.2"
                markerStart="url(#arrowStart)"
                markerEnd="url(#arr)"
              />
              <text
                x={plotLeftX - 5}
                y={(plotTopY + clayTopY) / 2 + 10}
                transform={`rotate(-90 ${plotLeftX + 24}, ${(plotTopY + clayTopY) / 2})`}
                fontSize="13"
                fill={text}
              >
                z1 = {fmt(Z1, 2)} m
              </text>
            </g>
          )}
          {/* z2 */}
          {Z1 !== null && Z2 !== null && Z2 > 0 && Z2 > Z1 && (
            <g>
              <line
                x1={plotLeftX + 84}
                y1={plotTopY}
                x2={plotLeftX + 84}
                y2={clayBottomY}
                stroke={line}
                strokeWidth="1"
                strokeDasharray="5 4"
              />
              <line
                x1={plotLeftX + 84}
                y1={plotTopY + 2}
                x2={plotLeftX + 84}
                y2={clayBottomY - 2}
                stroke={line}
                strokeWidth="1.2"
                markerStart="url(#arrowStart)"
                markerEnd="url(#arr)"
              />
              <text
                x={plotLeftX + 64}
                y={(plotTopY + clayBottomY) / 2}
                transform={`rotate(-90 ${plotLeftX + 64}, ${(plotTopY + clayBottomY) / 2})`}
                fontSize="13"
                fill={text}
              >
                z2 = {fmt(Z2, 2)} m
              </text>
            </g>
          )}
          {/* Foundation (only if visible) */}
          {!footingBelowView && (
            <>
              {/* Base */}
              <rect
                x={baseLeftX}
                y={baseTopY}
                width={baseWidthPx}
                height={baseThicknessPx}
                fill={conc}
                stroke={line}
                strokeWidth="1.25"
              />
              {/* Pedestal */}
              <rect
                x={pedestalLeftX}
                y={pedestalTopY}
                width={pedestalWidthPx}
                height={pedestalHeightPx}
                fill={conc}
                stroke={line}
                strokeWidth="1.25"
              />
              {/* q load arrows + label (shown only when Q is provided) */}
              {Q !== null && Q > 0 && (
                <g aria-label="Applied pressure q on foundation">
                  {/* label above the arrows */}
                  <text
                    x={baseLeftX + baseWidthPx / 2}
                    y={(baseTopY + baseUndersideY) / 2 + 2}
                    textAnchor="middle"
                    fontSize="12"
                    fill={text}
                  >
                    {`q = ${fmt(Q, 0)} kPa`}
                  </text>

                  {/* four downward arrows from mid-depth to bottom of foundation */}
                  {[0.2, 0.4, 0.6, 0.8].map((t, i) => {
                    const x = baseLeftX + baseWidthPx * t;
                    const yStart = (baseTopY + baseUndersideY) / 2 + 4; // mid-depth of footing
                    const yEnd = baseUndersideY - 1; // arrowhead touches bottom
                    return (
                      <line
                        key={`q-arr-${i}`}
                        x1={x}
                        y1={yStart}
                        x2={x}
                        y2={yEnd}
                        stroke={line}
                        strokeWidth="0.75"
                        markerEnd="url(#arr)"
                      />
                    );
                  })}
                </g>
              )}

              {/* B (drawn above base) */}
              {B !== null && B > 0 && (
                <g>
                  <line
                    x1={baseLeftX + 6}
                    y1={baseTopY - 12}
                    x2={baseLeftX + baseWidthPx - 6}
                    y2={baseTopY - 12}
                    stroke={line}
                    strokeWidth="1.2"
                    markerStart="url(#arrowStart)"
                    markerEnd="url(#arr)"
                  />
                  <text x={baseLeftX + baseWidthPx / 2} y={baseTopY - 18} textAnchor="middle" fontSize="13.5" fill={text}>
                    B = {fmt(B, 2)} m
                  </text>
                </g>
              )}
            </>
          )}
          {/* Notice when footing is below the visible block */}
          {footingBelowView && (
            <text x={(plotLeftX + plotRightX) / 2} y={plotBottomY - 16} textAnchor="middle" fontSize="13.5" fill={text}>
              Foundation base is below the current view (Df = {fmt(Df, 2)} m).
            </text>
          )}
          {/* Df (always shown; clamped to bottom of plot) */}
          {Df !== null && Df !== 0 && (
            <g>
              {(() => {
                const dimX = baseLeftX - 28;
                const yTop = plotTopY + 2;
                const yBottom = Math.min(baseUndersideY, plotBottomY) - 2;
                const midY = (yTop + yBottom) / 2;

                return (
                  <>
                    {/* guide line */}
                    <line
                      x1={dimX}
                      y1={plotTopY}
                      x2={dimX}
                      y2={Math.min(baseUndersideY, plotBottomY)}
                      stroke={line}
                      strokeWidth="1"
                      strokeDasharray="5 4"
                    />
                    {/* main dimension with arrows */}
                    <line
                      x1={dimX}
                      y1={yTop}
                      x2={dimX}
                      y2={yBottom}
                      stroke={line}
                      strokeWidth="1.2"
                      markerStart="url(#arrowStart)"
                      markerEnd="url(#arr)"
                    />
                    {/* centered label exactly between arrow tips */}
                    <g transform={`rotate(-90 ${dimX - 16} ${midY})`}>
                      <text x={dimX - 16} y={midY + 5} textAnchor="middle" fontSize="13" fill={text}>
                        Df = {fmt(Df, 2)} m
                      </text>
                    </g>
                  </>
                );
              })()}
            </g>
          )}
          {/* NEW: thickness from foundation underside to clay bottom: (z2 - Df) */}
          {/* H: thickness from foundation bottom to clay bottom (piecewise) */}
          {Z1 !== null && Z2 !== null && Df !== null && (
            <g>
              {(() => {
                const dimX = baseLeftX + baseWidthPx + 28; // place to the right of the base

                // true thickness value (not capped)
                let Hval = 0;
                if (Z1 !== null && Z2 !== null && Df !== null) {
                  if (Df <= Z1) {
                    Hval = Math.max(0, Z2 - Z1);
                  } else if (Df < Z2) {
                    Hval = Math.max(0, Z2 - Df);
                  } else {
                    Hval = 0;
                  }
                }

                // visual (frozen) drawing extents
                let yStart = null;
                let yEnd = null;
                if (Df !== null) {
                  if (Df <= (Z1V ?? Z1)) {
                    yStart = Math.min(clayTopY, plotBottomY);
                    yEnd = Math.min(clayBottomY, plotBottomY);
                  } else if (Df < (Z2V ?? Z2)) {
                    yStart = Math.min(baseUndersideY, plotBottomY);
                    yEnd = Math.min(clayBottomY, plotBottomY);
                  }
                }

                if (Hval > 0 && yStart !== null && yEnd !== null) {
                  const midY = (yStart + yEnd) / 2;
                  return (
                    <>
                      <line x1={dimX} y1={yStart} x2={dimX} y2={yEnd} stroke={line} strokeWidth="1" strokeDasharray="5 4" />
                      <line
                        x1={dimX}
                        y1={yStart + 2}
                        x2={dimX}
                        y2={yEnd - 2}
                        stroke={line}
                        strokeWidth="1.2"
                        markerStart="url(#arrowStart)"
                        markerEnd="url(#arr)"
                      />

                      <g transform={`rotate(-90 ${dimX - 16} ${midY})`}>
                        <text x={dimX - 16} y={midY + 35} textAnchor="middle" fontSize="13" fill={text}>
                          H = {fmt(Hval, 2)} m
                        </text>
                      </g>
                    </>
                  );
                }

                // show the note only when there is truly no clay below the foundation
                // show either the real H value or the no-clay note
                if (Hval > 0) {
                  return (
                    <text x={plotLeftX + 550} y={baseUndersideY - 10} fontSize="12.5" fill={text}>
                      {`H = ${fmt(Hval, 2)} m (below view)`}
                    </text>
                  );
                }
                if (Hval === 0) {
                  return (
                    <text x={plotLeftX + 550} y={baseUndersideY - 10} fontSize="12.5" fill={text}>
                      {'H = 0 m (No compressible clay below foundation)'}
                    </text>
                  );
                }
                return null;
              })()}
            </g>
          )}
        </svg>
      </div>
    </div>
  );
}
